import type { MonthlyTableCashFlowMonth } from 'features/drilldown/cashflows/gql/__generated__/MonthlyTableCashFlowMonth';
import type { AccountRemittanceRecordRow } from './getAccountRemittanceRecords_gql';
import { toNum } from './utils';

/**
 * Maps API remittance records to the shape expected by MonthlyTable.
 * payment_month is YYYY-MM; we output month (1-12) and the table uses year for display.
 */
function remittanceRecordToMonthlyRow(record: AccountRemittanceRecordRow): {
  year: number;
  row: MonthlyTableCashFlowMonth;
} {
  const [yearStr, monthStr] = record.payment_month.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);

  const starting_balance_cents = toNum(record.starting_balance_cents);
  const payment_cents = toNum(record.payment_cents);
  const principal_cents = toNum(record.principal_cents);
  const interest_cents = toNum(record.interest_cents);
  const servicing_cents = toNum(record.servicing_cents);
  const prepayments_cents = toNum(record.prepayments_cents);
  const defaults_cents = toNum(record.defaults_cents);
  const losses_cents = toNum(record.losses_cents);
  const ending_balance_cents = toNum(record.current_balance_cents);

  const cash_flow_cents =
    principal_cents +
    interest_cents -
    servicing_cents +
    prepayments_cents -
    defaults_cents -
    losses_cents;

  return {
    year,
    row: {
      month,
      starting_balance_cents,
      payments_cents: payment_cents,
      principal_cents,
      interest_cents,
      servicing_cents,
      prepayments_cents,
      defaults_cents,
      losses_cents,
      ending_balance_cents,
      cash_flow_cents,
    },
  };
}

/**
 * Converts all records to monthly rows and returns rows for the given year, sorted by month.
 */
export function recordsToMonthlyTableRows(
  records: AccountRemittanceRecordRow[],
  selectedYear: number | null,
): MonthlyTableCashFlowMonth[] {
  const withYear = records.map(remittanceRecordToMonthlyRow);
  const filtered =
    selectedYear != null ? withYear.filter(({ year }) => year === selectedYear) : withYear;
  const sorted = [...filtered].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.row.month - b.row.month;
  });
  return sorted.map(({ row }) => row);
}

/**
 * Returns distinct years from records (payment_month YYYY-MM), sorted ascending.
 */
export function getAvailableYears(records: AccountRemittanceRecordRow[]): number[] {
  const years = new Set<number>();
  for (const r of records) {
    const y = parseInt(r.payment_month.slice(0, 4), 10);
    if (!Number.isNaN(y)) years.add(y);
  }
  return Array.from(years).sort((a, b) => a - b);
}
